import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, Loader2, Target, Search, Maximize2, Minimize2 } from 'lucide-react';
import { api, fileURL } from '../lib/api';
import { toast } from 'sonner';

const km = (la1, lo1, la2, lo2) => Math.hypot(la1 - la2, (lo1 - lo2) * Math.cos(la1 * Math.PI / 180)) * 111;

const makeIcon = ({ logo, size = 26, bg = '#1c1c1c', ring = null, dim = false }) => {
  const inner = logo
    ? `<img src="${logo}" style="width:${size - 8}px;height:${size - 8}px;object-fit:contain;border-radius:50%;background:#fff"/>`
    : `<div style="width:${Math.round(size / 3.5)}px;height:${Math.round(size / 3.5)}px;background:#f8f6f1;border-radius:50%"></div>`;
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${logo ? '#fff' : bg};border:2.5px solid ${ring || '#f8f6f1'};box-shadow:0 3px 8px rgba(0,0,0,${dim ? 0.12 : 0.3});display:flex;align-items:center;justify-content:center;opacity:${dim ? 0.55 : 1}">${inner}</div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
};

// Encuadra la estación + todos sus markers adoptados al abrir (una sola vez)
const FitToMarkers = ({ station, markers }) => {
  const map = useMap();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done || markers.length === 0) return;
    const pts = [[station.lat, station.lng], ...markers.map(m => [m.lat, m.lng])];
    map.fitBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 14 });
    setDone(true);
  }, [done, map, station, markers]);
  return null;
};

const InvalidateOnResize = ({ dep }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [dep, map]);
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], 16); }, [target, map]);
  return null;
};

export const StationMarkersMap = ({ station, onClose, onChanged }) => {
  const [stations, setStations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [markerIds, setMarkerIds] = useState(station.marker_station_ids || []);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ctx, b] = await Promise.all([
        api.get(`/stations/${station.id}/map-context`),
        api.get('/brands'),
      ]);
      const list = ctx.data.stations || [];
      setStations(list);
      setBrands(b.data);
      // Podar IDs fantasma (estaciones eliminadas) para que el contador y el
      // cupo de 6 reflejen solo markers reales
      const raw = ctx.data.station?.marker_station_ids || [];
      const validSet = new Set(list.map(s => s.id));
      const cleanIds = raw.filter(id => validSet.has(id));
      setMarkerIds(cleanIds);
      if (cleanIds.length !== raw.length) {
        api.put(`/stations/${station.id}`, { marker_station_ids: cleanIds }).then(() => onChanged?.()).catch(() => {});
        toast.info(`${raw.length - cleanIds.length} marker(s) apuntaban a estaciones eliminadas y se quitaron`);
      }
    } catch { toast.error('No se pudo cargar el mapa'); }
    finally { setLoading(false); }
  }, [station.id]);

  useEffect(() => { load(); }, [load]);

  const logoOf = useCallback((brand) => {
    const b = brands.find(x => x.name.toLowerCase() === (brand || '').toLowerCase());
    return b?.logo_url ? fileURL(b.logo_url) : null;
  }, [brands]);

  // Build reverse index: competitor id → list of OTHER own stations that use it as marker.
  // Lets us surface competitors that show up on the capturer's route because they belong
  // to a different own station (avoids "why is this pin missing from El Faro's map" confusion).
  const markerOfOthers = useMemo(() => {
    const map = new Map();
    stations.forEach(s => {
      if (!s.is_own || s.id === station.id) return;
      (s.marker_station_ids || []).forEach(mid => {
        if (!map.has(mid)) map.set(mid, []);
        map.get(mid).push(s);
      });
    });
    return map;
  }, [stations, station.id]);

  // Show: (i) always-adopted markers of THIS station,
  //       (ii) competitors used as marker by any OTHER own station (so the capturer's route matches),
  //       (iii) any station (own or competitor) within 15 km of the selected station.
  const others = useMemo(() => (
    stations.filter(s => s.id !== station.id && (
      markerIds.includes(s.id) ||
      markerOfOthers.has(s.id) ||
      km(station.lat, station.lng, s.lat, s.lng) <= 15
    ))
  ), [stations, station.id, station.lat, station.lng, markerIds, markerOfOthers]);

  const toggleMarker = async (comp) => {
    const isMarker = markerIds.includes(comp.id);
    const next = isMarker ? markerIds.filter(x => x !== comp.id) : [...markerIds, comp.id];
    if (next.length > 6) { toast.error('Máximo 6 markers por estación'); return; }
    setBusyId(comp.id);
    try {
      await api.put(`/stations/${station.id}`, { marker_station_ids: next });
      setMarkerIds(next);
      toast.success(isMarker ? `${comp.name} quitado de markers` : `${comp.name} agregado como marker`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error actualizando markers'); }
    finally { setBusyId(null); }
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return [station, ...stations]
      .filter(s => s.lat && s.lng && `${s.name} ${s.brand || ''}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, stations, station]);

  const jumpTo = (s) => {
    setFlyTarget({ lat: s.lat, lng: s.lng, id: s.id });
    setSearch('');
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${expanded ? 'p-0' : 'p-4'}`} data-testid="station-map-overlay">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white shadow-2xl w-full flex flex-col overflow-hidden ${expanded ? 'h-full max-w-none rounded-none' : 'max-w-5xl h-[82vh] rounded-2xl'}`}>
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#e8e5de]">
          <div className="flex items-center gap-2.5 min-w-0">
            <Target size={16} className="text-[#0f1d6f] shrink-0"/>
            <div className="min-w-0">
              <p className="text-[14px] font-medium leading-tight truncate">{station.name}</p>
              <p className="text-[11px] text-[#8a8579]">Markers: <b data-testid="map-marker-count">{markerIds.length}/6</b> · click en un pin para agregar o quitar</p>
            </div>
          </div>
          <div className="relative flex-1 max-w-[280px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar estación en el mapa…"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#e8e5de] text-[12.5px] focus:outline-none focus:border-[#0f1d6f]"
              data-testid="map-search-input"
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e8e5de] rounded-lg shadow-lg z-[600] max-h-56 overflow-y-auto" data-testid="map-search-results">
                {searchResults.map(s => (
                  <button key={s.id} onClick={() => jumpTo(s)}
                    className="w-full text-left px-3 py-2 hover:bg-[#f8f6f1] border-b border-[#f3f0e9] last:border-0"
                    data-testid={`map-search-result-${s.id}`}>
                    <p className="text-[12px] font-medium truncate">{s.name}</p>
                    <p className="text-[10.5px] text-[#8a8579]">
                      {s.brand}
                      {s.id === station.id ? ' · estación seleccionada' : s.is_own ? ' · propia' : ''}
                      {markerIds.includes(s.id) ? ' · MARKER' : ''}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setExpanded(x => !x)}
            className="w-8 h-8 rounded-full hover:bg-[#f3f0e9] flex items-center justify-center shrink-0"
            title={expanded ? 'Restaurar tamaño' : 'Agrandar mapa'}
            data-testid="expand-map-btn"
          >
            {expanded ? <Minimize2 size={15}/> : <Maximize2 size={15}/>}
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#f3f0e9] flex items-center justify-center shrink-0" data-testid="close-map-btn"><X size={16}/></button>
        </div>
        <div className="flex-1 relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
          ) : (
            <MapContainer center={[station.lat, station.lng]} zoom={14} className="w-full h-full">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap'/>
              <FitToMarkers station={station} markers={others.filter(s => markerIds.includes(s.id) && s.lat && s.lng)} />
              <FlyTo target={flyTarget} />
              <InvalidateOnResize dep={expanded} />
              <Marker position={[station.lat, station.lng]} icon={makeIcon({ logo: logoOf(station.brand), size: 36, bg: '#0f1d6f', ring: '#0f1d6f' })}>
                <Popup><div className="text-[12px]"><p className="font-medium">{station.name}</p>{station.bu && <p className="font-mono text-[11px] bg-[#e7eeff] text-[#0f1d6f] rounded px-1.5 py-0.5 inline-block my-0.5">BU {station.bu}</p>}<p className="text-[#5a5a5a]">Estación propia seleccionada</p></div></Popup>
              </Marker>
              {others.map(s => {
                const isMarker = markerIds.includes(s.id);
                const otherOwners = markerOfOthers.get(s.id) || [];
                const isMarkerOfOther = !isMarker && !s.is_own && otherOwners.length > 0;
                const ring = isMarker
                  ? '#b91c1c'
                  : isMarkerOfOther
                    ? '#f97316'
                    : (s.is_own ? '#3b6cff' : '#f8f6f1');
                const bg = s.is_own ? '#3b6cff' : '#1c1c1c';
                const size = isMarker ? 32 : (isMarkerOfOther ? 28 : (s.is_own ? 22 : 26));
                const icon = makeIcon({ logo: logoOf(s.brand), size, bg, ring });
                return (
                  <Marker key={s.id} position={[s.lat, s.lng]} icon={icon}>
                    <Popup>
                      <div className="text-[12px] min-w-[180px]">
                        <p className="font-medium">{s.name}</p>
                        {s.bu && (
                          <p className="font-mono text-[11px] bg-[#f3f0e9] border border-[#e8e5de] rounded px-1.5 py-0.5 inline-block my-0.5" data-testid={`map-popup-bu-${s.id}`}>BU {s.bu}</p>
                        )}
                        <p className="text-[#5a5a5a] mb-1">
                          {s.brand}
                          {s.is_own ? ' · propia' : ''}
                          {isMarker ? ' · MARKER' : ''}
                        </p>
                        {isMarkerOfOther && (
                          <p className="text-[10.5px] text-[#c2410c] bg-[#fff7ed] border border-[#fed7aa] rounded px-1.5 py-0.5 mb-1.5">
                            Marker de: {otherOwners.map(o => o.name).join(' · ')}
                          </p>
                        )}
                        <button onClick={() => toggleMarker(s)} disabled={busyId === s.id}
                          className={`w-full px-2 py-1.5 rounded text-[11.5px] text-white ${isMarker ? 'bg-[#b91c1c]' : 'bg-[#1c1c1c]'} disabled:opacity-50 mt-1`}
                          data-testid={`map-toggle-marker-${s.id}`}>
                          {busyId === s.id ? '…' : isMarker ? 'Quitar marker' : 'Agregar como marker'}
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>
        <div className="px-5 py-2.5 border-t border-[#e8e5de] flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#5a5a5a]">
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#0f1d6f] mr-1.5"/>Estación seleccionada</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#3b6cff] mr-1.5"/>Propias (click para agregar/quitar)</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1c1c1c] ring-2 ring-[#b91c1c] mr-1.5"/>Markers actuales</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1c1c1c] ring-2 ring-[#f97316] mr-1.5"/>Marker de otra propia</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1c1c1c] mr-1.5"/>Competencia (click para agregar/quitar)</span>
        </div>
      </div>
    </div>
  );
};

export default StationMarkersMap;
